import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import PhoneInput from '../../components/elements/phone_input';
import Button from '../../components/elements/button';
import FileUpload from '../../components/elements/file_upload';
import EmailFirstAuth from '../../components/auth/EmailFirstAuth';
import { Truck, CircleCheck, MapPin, Clock } from '../../assets/icons';
import '../../assets/css/logistics/register.css';
import { logisticsApi } from '../../network/apis/logistics';
import { saveDraft, loadDraft, clearDraft, getDraftAge } from '../../utils/registrationDraft';

const NIGERIAN_STATES = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
    'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
    'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const SERVICE_TYPES = [
    { id: 'LOCAL', name: 'Local Delivery' },
    { id: 'REGIONAL', name: 'Regional Delivery' },
    { id: 'NATIONAL', name: 'National Delivery' },
    { id: 'INTERNATIONAL', name: 'International Delivery' }
];

const VEHICLE_TYPES = [
    { id: 'MOTORCYCLE', name: 'Motorcycle' },
    { id: 'CAR', name: 'Car/Small Van' },
    { id: 'VAN', name: 'Van' },
    { id: 'TRUCK', name: 'Truck' },
    { id: 'COLD_CHAIN', name: 'Cold Chain/Refrigerated' }
];

const LogisticsRegister = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [draftAge, setDraftAge] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [, setCurrentUser] = useState(null);
    
    const [formData, setFormData] = useState({
        // Company Info
        companyName: '',
        companyDescription: '',
        companyLogo: null,
        companyLogo_name: '',
        registrationNumber: '',
        taxId: '',
        
        // Contact Info
        contactEmail: '',
        contactPhone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        
        // Service Details
        coverageAreas: [],
        serviceTypes: [],
        vehicleTypes: [],
        maxWeightCapacity: '',
        
        // Operating Hours
        operatingHoursFrom: '08:00',
        operatingHoursTo: '18:00',
        worksWeekends: false,
        worksHolidays: false,
        
        // Banking
        bankName: '',
        accountNumber: '',
        accountName: '',
        
        // Documents
        businessCertificate: null,
        businessCertificate_name: '',
        insuranceCertificate: null,
        insuranceCertificate_name: '',
        identityDocument: null,
        identityDocument_name: ''
    });

    const totalSteps = 6; // Auth, Company Info, Contact, Services, Banking, Documents

    // Check if already authenticated on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
            const userData = JSON.parse(user);
            if (userData.is_logistics) {
                navigate('/logistics');
                return;
            }
            setIsAuthenticated(true);
            setCurrentUser(userData);
            setCurrentStep(2);
        }
    }, [navigate]);
    
    // Check for existing draft on mount
    useEffect(() => {
        const draft = loadDraft('logistics');
        if (draft && draft.data.companyName) {
            setDraftAge(getDraftAge('logistics'));
            setShowDraftModal(true);
        }
    }, []);
    
    // Auto-save when form data changes
    useEffect(() => {
        if (currentStep > 1 && formData.companyName) {
            saveDraft('logistics', formData, currentStep);
        }
    }, [formData, currentStep]);
    
    const handleRestoreDraft = () => {
        const draft = loadDraft('logistics');
        if (draft) {
            setFormData(prev => ({ ...prev, ...draft.data }));
            if (isAuthenticated && draft.currentStep > 1) {
                setCurrentStep(draft.currentStep);
            }
            setSuccess('Draft restored successfully!');
        }
        setShowDraftModal(false);
    };
    
    const handleDiscardDraft = () => {
        clearDraft('logistics');
        setShowDraftModal(false);
    };
    
    const handleAuthSuccess = (user, { isNewUser }) => {
        setIsAuthenticated(true);
        setCurrentUser(user);
        
        if (user.is_logistics) {
            setSuccess('You are already registered as a logistics company! Redirecting...');
            setTimeout(() => navigate('/logistics'), 1500);
            return;
        }
        
        setSuccess(isNewUser ? 'Account created! Now let\'s set up your logistics company.' : 'Welcome back! Let\'s set up your logistics company.');
        
        setTimeout(() => {
            setCurrentStep(2);
            setError(null);
            setSuccess(null);
        }, 1000);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleArrayToggle = (field, value) => {
        setFormData(prev => {
            const currentArray = prev[field] || [];
            const index = currentArray.indexOf(value);
            if (index > -1) {
                return { ...prev, [field]: currentArray.filter(item => item !== value) };
            }
            return { ...prev, [field]: [...currentArray, value] };
        });
    };

    const handleFileUpload = (field, file) => {
        if (file === null) {
            setFormData({
                ...formData,
                [field]: null,
                [field + '_name']: ''
            });
            return;
        }
        setFormData({
            ...formData,
            [field]: file,
            [field + '_name']: file.name
        });
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        setSuccess(null);
        
        if (!isAuthenticated) {
            setError('Please sign in or create an account first');
            setCurrentStep(1);
            return;
        }
        
        setIsSubmitting(true);
        setSuccess('Submitting your logistics company registration...');
        
        try {
            const logisticsFormData = new FormData();
            
            // Company Info
            logisticsFormData.append('company_name', formData.companyName);
            logisticsFormData.append('company_description', formData.companyDescription);
            logisticsFormData.append('registration_number', formData.registrationNumber);
            logisticsFormData.append('tax_id', formData.taxId || '');
            
            // Contact Info
            logisticsFormData.append('contact_email', formData.contactEmail);
            logisticsFormData.append('contact_phone', formData.contactPhone);
            logisticsFormData.append('website', formData.website || '');
            logisticsFormData.append('address', formData.address);
            logisticsFormData.append('city', formData.city);
            logisticsFormData.append('state', formData.state);
            logisticsFormData.append('postal_code', formData.postalCode || '');
            
            // Service Details
            logisticsFormData.append('coverage_areas', JSON.stringify(formData.coverageAreas));
            logisticsFormData.append('service_types', JSON.stringify(formData.serviceTypes));
            logisticsFormData.append('vehicle_types', JSON.stringify(formData.vehicleTypes));
            logisticsFormData.append('max_weight_capacity', formData.maxWeightCapacity);
            
            // Operating Hours
            logisticsFormData.append('operating_hours_from', formData.operatingHoursFrom);
            logisticsFormData.append('operating_hours_to', formData.operatingHoursTo);
            logisticsFormData.append('works_weekends', formData.worksWeekends);
            logisticsFormData.append('works_holidays', formData.worksHolidays);
            
            // Banking
            logisticsFormData.append('bank_name', formData.bankName);
            logisticsFormData.append('account_number', formData.accountNumber);
            logisticsFormData.append('account_name', formData.accountName);
            
            // Files
            if (formData.companyLogo) {
                logisticsFormData.append('company_logo', formData.companyLogo);
            }
            if (formData.businessCertificate) {
                logisticsFormData.append('business_certificate', formData.businessCertificate);
            }
            if (formData.insuranceCertificate) {
                logisticsFormData.append('insurance_certificate', formData.insuranceCertificate);
            }
            if (formData.identityDocument) {
                logisticsFormData.append('identity_document', formData.identityDocument);
            }
            
            await logisticsApi.registerCompany(logisticsFormData);
            
            clearDraft('logistics');
            
            setSuccess('Registration submitted successfully! Your application is under review.');
            
            setTimeout(() => {
                navigate('/logistics/pending');
            }, 2000);
            
        } catch (err) {
            console.error('Registration error:', err);
            const errorMessage = err.response?.data?.message || 
                                err.response?.data?.errors || 
                                'Failed to submit registration. Please try again.';
            setError(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                return isAuthenticated;
            case 2:
                return formData.companyName && formData.companyDescription && formData.registrationNumber;
            case 3:
                return formData.contactEmail && formData.contactPhone && formData.address && formData.city && formData.state;
            case 4:
                return formData.coverageAreas.length > 0 && formData.serviceTypes.length > 0 && 
                       formData.vehicleTypes.length > 0 && formData.maxWeightCapacity;
            case 5:
                return formData.bankName && formData.accountNumber && formData.accountName;
            case 6:
                return formData.businessCertificate && formData.identityDocument;
            default:
                return false;
        }
    };

    const handleNext = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        
        if (!validateStep(currentStep)) {
            setError('Please fill in all required fields before proceeding.');
            return;
        }
        
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => {
        if (currentStep > 2) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        } else if (currentStep === 2 && !isAuthenticated) {
            setCurrentStep(1);
        }
    };

    const renderProgressBar = () => (
        <div className="logistics-progress">
            {[...Array(totalSteps)].map((_, index) => (
                <div 
                    key={index + 1}
                    className={`progress-step ${currentStep > index ? 'completed' : ''} ${currentStep === index + 1 ? 'active' : ''}`}
                >
                    <div className="step-number">
                        {currentStep > index + 1 ? <img src={CircleCheck} alt="" width={20} height={20} /> : index + 1}
                    </div>
                    <span className="step-label">
                        {['Auth', 'Company', 'Contact', 'Services', 'Banking', 'Documents'][index]}
                    </span>
                </div>
            ))}
        </div>
    );

    const renderAuthStep = () => (
        <div className="logistics-form-section">
            <h2>Create Your Account</h2>
            <p className="section-description">
                Start by signing in or creating an account. This will be your login for managing your logistics company.
            </p>
            <EmailFirstAuth 
                onAuthSuccess={handleAuthSuccess}
                userType="logistics"
            />
        </div>
    );

    const renderCompanyInfoStep = () => (
        <div className="logistics-form-section">
            <h2><img src={Truck} alt="" width={24} height={24} /> Company Information</h2>
            
            <FormInput
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Enter your logistics company name"
                required
            />
            
            <div className="form-group">
                <label>Company Description</label>
                <textarea
                    name="companyDescription"
                    value={formData.companyDescription}
                    onChange={handleInputChange}
                    placeholder="Describe your logistics services, capabilities, and experience..."
                    rows={4}
                    required
                />
            </div>
            
            <FileUpload
                label="Company Logo"
                field="companyLogo"
                accept="image/*"
                onUpload={handleFileUpload}
                currentFile={formData.companyLogo_name}
                hint="Upload your company logo (PNG, JPG, max 5MB)"
            />
            
            <FormInput
                label="Business Registration Number"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
                placeholder="CAC Registration Number"
                required
            />
            
            <FormInput
                label="Tax ID (Optional)"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                placeholder="Tax Identification Number"
            />
        </div>
    );

    const renderContactStep = () => (
        <div className="logistics-form-section">
            <h2><img src={MapPin} alt="" width={24} height={24} /> Contact & Location</h2>
            
            <FormInput
                label="Business Email"
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="contact@yourcompany.com"
                required
            />
            
            <PhoneInput
                label="Business Phone"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="+234 800 000 0000"
                required
            />
            
            <FormInput
                label="Website (Optional)"
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://www.yourcompany.com"
            />
            
            <div className="form-group">
                <label>Business Address</label>
                <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your full business address"
                    rows={3}
                    required
                />
            </div>
            
            <div className="form-row">
                <FormInput
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="e.g., Lagos"
                    required
                />
                
                <div className="form-group">
                    <label>State</label>
                    <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Select State</option>
                        {NIGERIAN_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                        ))}
                    </select>
                </div>
            </div>
            
            <FormInput
                label="Postal Code (Optional)"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                placeholder="e.g., 100001"
            />
        </div>
    );

    const renderServicesStep = () => (
        <div className="logistics-form-section">
            <h2><img src={Truck} alt="" width={24} height={24} /> Services & Coverage</h2>
            
            <div className="form-group">
                <label>Coverage Areas <span className="required">*</span></label>
                <p className="help-text">Select the states where you provide delivery services</p>
                <div className="checkbox-grid">
                    {NIGERIAN_STATES.map(state => (
                        <label key={state} className="checkbox-item">
                            <input
                                type="checkbox"
                                checked={formData.coverageAreas.includes(state)}
                                onChange={() => handleArrayToggle('coverageAreas', state)}
                            />
                            <span>{state}</span>
                        </label>
                    ))}
                </div>
            </div>
            
            <div className="form-group">
                <label>Service Types <span className="required">*</span></label>
                <div className="checkbox-list">
                    {SERVICE_TYPES.map(service => (
                        <label key={service.id} className="checkbox-item">
                            <input
                                type="checkbox"
                                checked={formData.serviceTypes.includes(service.id)}
                                onChange={() => handleArrayToggle('serviceTypes', service.id)}
                            />
                            <span>{service.name}</span>
                        </label>
                    ))}
                </div>
            </div>
            
            <div className="form-group">
                <label>Vehicle Types <span className="required">*</span></label>
                <div className="checkbox-list">
                    {VEHICLE_TYPES.map(vehicle => (
                        <label key={vehicle.id} className="checkbox-item">
                            <input
                                type="checkbox"
                                checked={formData.vehicleTypes.includes(vehicle.id)}
                                onChange={() => handleArrayToggle('vehicleTypes', vehicle.id)}
                            />
                            <span>{vehicle.name}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="form-group">
                <label>Maximum Weight Capacity (kg) <span className="required">*</span></label>
                <p className="help-text">Indicate the maximum weight your fleet can handle per shipment</p>
            
            <FormInput
                label="Maximum Weight Capacity (kg)"
                type="number"
                name="maxWeightCapacity"
                value={formData.maxWeightCapacity}
                onChange={handleInputChange}
                placeholder="e.g., 5000"
                required
            />
            </div>
            
            <div className="form-group">
                <label><img src={Clock} alt="" width={18} height={18} /> Operating Hours</label>
                <div className="form-row">
                    <FormInput
                        label="From"
                        type="time"
                        name="operatingHoursFrom"
                        value={formData.operatingHoursFrom}
                        onChange={handleInputChange}
                    />
                    <FormInput
                        label="To"
                        type="time"
                        name="operatingHoursTo"
                        value={formData.operatingHoursTo}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
            
            <div className="form-group checkbox-inline">
                <label>
                    <input
                        type="checkbox"
                        name="worksWeekends"
                        checked={formData.worksWeekends}
                        onChange={handleInputChange}
                    />
                    <span>Available on Weekends</span>
                </label>
                <label>
                    <input
                        type="checkbox"
                        name="worksHolidays"
                        checked={formData.worksHolidays}
                        onChange={handleInputChange}
                    />
                    <span>Available on Public Holidays</span>
                </label>
            </div>
        </div>
    );

    const renderBankingStep = () => (
        <div className="logistics-form-section">
            <h2>Banking Information</h2>
            <p className="section-description">
                Enter your bank details for receiving payments from shipments.
            </p>
            
            <FormInput
                label="Bank Name"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                placeholder="e.g., First Bank"
                required
            />
            
            <FormInput
                label="Account Number"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                placeholder="Enter your 10-digit account number"
                required
            />
            
            <FormInput
                label="Account Name"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                placeholder="Name on bank account"
                required
            />
        </div>
    );

    const renderDocumentsStep = () => (
        <div className="logistics-form-section">
            <h2>Documents & Verification</h2>
            <p className="section-description">
                Upload the required documents for verification. All documents will be kept confidential.
            </p>
            
            <FileUpload
                label="Business Registration Certificate"
                field="businessCertificate"
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleFileUpload}
                currentFile={formData.businessCertificate_name}
                hint="CAC Certificate or Business Registration Document (PDF, JPG, PNG)"
                required
            />
            
            <FileUpload
                label="Insurance Certificate (Optional)"
                field="insuranceCertificate"
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleFileUpload}
                currentFile={formData.insuranceCertificate_name}
                hint="Goods-in-transit insurance or fleet insurance certificate"
            />
            
            <FileUpload
                label="Identity Document"
                field="identityDocument"
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleFileUpload}
                currentFile={formData.identityDocument_name}
                hint="Valid government-issued ID of the company representative"
                required
            />
            
            <div className="terms-agreement">
                <p>
                    By submitting this registration, you agree to our{' '}
                    <a href="/logistics-terms" target="_blank" rel="noopener noreferrer">
                        Logistics Partner Agreement
                    </a>
                    {' '}and{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                    </a>.
                </p>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return renderAuthStep();
            case 2:
                return renderCompanyInfoStep();
            case 3:
                return renderContactStep();
            case 4:
                return renderServicesStep();
            case 5:
                return renderBankingStep();
            case 6:
                return renderDocumentsStep();
            default:
                return null;
        }
    };

    return (
        <div className="logistics-register-page">
            <div className="logistics-register-container">
                <div className="logistics-register-header">
                    <img src={Truck} alt="" width={48} height={48} className="header-icon" />
                    <h1>Register as a Logistics Partner</h1>
                    <p>Join our network of delivery partners and expand your business</p>
                </div>
                
                {renderProgressBar()}
                
                {showDraftModal && (
                    <div className="draft-modal">
                        <div className="draft-modal-content">
                            <h3>Continue Your Registration?</h3>
                            <p>
                                You have an unfinished registration from {draftAge}. 
                                Would you like to continue where you left off?
                            </p>
                            <div className="draft-modal-actions">
                                <Button onClick={handleRestoreDraft} variant="primary" text="Continue" />
                                <Button onClick={handleDiscardDraft} variant="outline" text="Start Fresh" />
                            </div>
                        </div>
                    </div>
                )}
                
                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}
                
                <form onSubmit={currentStep === totalSteps ? handleSubmit : handleNext}>
                    {renderCurrentStep()}
                    
                    {currentStep > 1 && (
                        <div className="form-navigation">
                            <Button 
                                type="button" 
                                onClick={handleBack}
                                variant="outline"
                                text="Back"
                            />
                            
                            {currentStep < totalSteps ? (
                                <Button 
                                    type="submit"
                                    variant="primary"
                                    disabled={!validateStep(currentStep)}
                                    text="Next"
                                />
                            ) : (
                                <Button 
                                    type="submit"
                                    variant="primary"
                                    disabled={isSubmitting || !validateStep(currentStep)}
                                    text={isSubmitting ? 'Submitting...' : 'Submit Registration'}
                                    loading={isSubmitting}
                                />
                            )}
                        </div>
                    )}
                </form>
                
                <div className="logistics-benefits">
                    <h3>Why Partner With Us?</h3>
                    <div className="benefits-grid">
                        <div className="benefit-item">
                            <div className="benefit-icon">📦</div>
                            <h4>Growing Market</h4>
                            <p>Access a growing market of aquaculture farmers and vendors</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">💰</div>
                            <h4>Competitive Rates</h4>
                            <p>Set your own rates and earn on every delivery</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">🔒</div>
                            <h4>Secure Payments</h4>
                            <p>Guaranteed payments directly to your bank account</p>
                        </div>
                        <div className="benefit-item">
                            <div className="benefit-icon">📱</div>
                            <h4>Easy Management</h4>
                            <p>Dashboard to manage shipments, rates, and earnings</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogisticsRegister;
